import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { 
    Plus, Search, FileText, ChevronLeft, ChevronRight, 
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const POList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // 🎯 إعدادات الترجمة والاتجاه
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate } = usePermissions('po');
    const [showAllSites, setShowAllSites] = useState(false);
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    // --- State الأساسي ---
    const [pos, setPos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';
    
    // --- Params State ---
    const searchParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [limit] = useState(50);
    const [sort, setSort] = useState({ 
        column: searchParams.get('sortBy') || 'orderdate', 
        direction: searchParams.get('sortDir') || 'desc' 
    });

    // --- 🎯 فلاتر الأعمدة ---
    const [filters, setFilters] = useState({
        ponum: searchParams.get('ponum') || "",
        description: searchParams.get('description') || "",
        status: searchParams.get('status') || "",
        vendor: searchParams.get('vendor') || "",
        siteid: searchParams.get('siteid') || ""
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/po', {
                params: {
                    page,
                    limit,
                    all_sites: Boolean(showAllSites), 
                    user_siteid: sessionSite,
                    orgid: sessionOrg,
                    filter_siteid: filters.siteid || "", 
                    sortBy: sort.column,
                    sortDir: sort.direction,
                    ponum: filters.ponum,
                    description: filters.description,
                    status: filters.status,
                    vendor: filters.vendor,
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setPos(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            toast.error(t('po.messages.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [page, showAllSites, filters, sort, sessionSite, sessionOrg, limit, t]);

    useEffect(() => {
        const timer = setTimeout(() => { fetchData(); }, 400); 
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1); 
    };

    const handleRowClick = (ponum) => {
        const queryParams = new URLSearchParams({
            page,
            ...filters,
            sortBy: sort.column,
            sortDir: sort.direction,
            all_sites: showAllSites,
            orgid: sessionOrg
        }).toString();
        navigate(`/po/${ponum}?${queryParams}`);
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const clearFilters = () => {
        setFilters({ ponum: "", description: "", status: "", vendor: "", siteid: "" });
        setPage(1);
    };

    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };
        
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div 
            className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo dir-rtl text-right' : 'text-left'}`} 
            style={{ fontSize: `${fontSize}px` }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            
            {/* Toolbar */}
            <div className="bg-white p-3 border-b shadow-sm z-20">
                <div className={`flex justify-between items-center px-4 `}>
                    <h2 className={`text-[#1a3353] font-black italic uppercase text-lg flex items-center gap-2 `}>
                        <FileText size={22} className="text-blue-600" /> 
                        {t('po.headers.poList')}
                        <RefreshCw 
                            size={18} 
                            className={`cursor-pointer transition-all ${loading ? 'animate-spin text-orange-500' : 'text-blue-400 hover:text-blue-600'} ${isRTL ? 'mr-2' : 'ml-2'}`} 
                            onClick={fetchData} 
                        />
                    </h2>
                    
                    <div className={`flex gap-3 `}>
                        <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-gray-500 hover:text-red-600 font-bold transition-colors">
                            <X size={16}/> {t('po.actions.clearFilters')}
                        </button>
                        {canCreate && (
                            <button onClick={() => navigate('/po/new')} className={`bg-[#1a3353] text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-blue-900 shadow-lg active:scale-95 transition-all `}>
                                <Plus size={16} /> {t('po.actions.newPO')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-white border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-[#1a3353] text-white font-bold uppercase sticky top-0 z-10">
                            {/* Header Labels */}
                            <tr className={isRTL ? '' : ''}>
                                <SortableHeader label={t('po.tables.poNum')} sortKey="ponum" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('assets.headers.desc')} sortKey="description" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <th className="p-3 border-x border-white/10 text-center">{t('assets.headers.status')}</th>
                                <SortableHeader label={t('po.tables.totalCost')} sortKey="totalcost" currentSort={sort} onSort={handleSort} align="right" isRTL={isRTL} />
                                <SortableHeader label={t('po.tables.orderDate')} sortKey="orderdate" currentSort={sort} onSort={handleSort} align="center" isRTL={isRTL} />
                                <th className="p-3 text-center border-x border-white/10">{t('assets.headers.site')}</th>
                            </tr>
                            {/* Filter Row */}
                            <tr className={`bg-[#f8fafc] shadow-inner `}>
                                <td className="p-1 border-x border-gray-200">
                                    <input className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.ponum} onChange={e => handleFilterChange('ponum', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className={`w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.description} onChange={e => handleFilterChange('description', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-gray-200">
                                    <select className={`w-full p-1.5 text-blue-900 font-black outline-none border border-gray-100 bg-white ${isRTL ? 'dir-rtl text-right' : ''}`} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                                        <option value="">{t('common.all')}</option>
                                        <option value="WAPPR">WAPPR</option>
                                        <option value="APPR">APPR</option>
                                        <option value="COMPLETE">COMPLETE</option>
                                        <option value="CLOSE">CLOSE</option>
                                    </select>
                                </td>
                                <td className="p-1 border-x border-gray-200 bg-gray-100/50"></td>
                                <td className="p-1 border-x border-gray-200 bg-gray-100/50"></td>
                                <td className="p-1 border-x border-gray-200">
                                    <input className="w-full p-1.5 text-blue-900 font-bold outline-none border border-gray-100 focus:border-blue-400 bg-white text-center" placeholder={t('common.filter')} value={filters.siteid} onChange={e => handleFilterChange('siteid', e.target.value)} />
                                </td>
                            </tr>
                        </thead>
                        
                        <tbody className="divide-y font-bold">
                            {loading ? (
                                <tr><td colSpan="6" className="p-20 text-center text-gray-300 font-black animate-pulse uppercase tracking-[0.2em]">{t('po.messages.syncing')}</td></tr>
                            ) : pos.length > 0 ? (
                                pos.map((po, i) => (
                                    <tr key={i} className={`hover:bg-blue-50 cursor-pointer transition-colors group ${isRTL ? ' text-right' : 'text-left'}`} onClick={() => handleRowClick(po.ponum)}>
                                        <td className={`p-3 border-x font-black text-blue-900 group-hover:underline ${isRTL ? 'border-l' : 'border-r'}`}>{po.ponum}</td>
                                        <td className={`p-3 border-x text-gray-600 font-bold italic ${isRTL ? 'border-l' : 'border-r'}`}>{po.description}</td>
                                        <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                                            <span className={`px-3 py-1 rounded-full font-black text-[9px] shadow-sm border ${getStatusStyle(po.status)}`}>
                                                {po.status}
                                            </span>
                                        </td>
                                        <td className={`p-3 border-x font-mono text-green-700 font-black italic bg-gray-50/30 ${isRTL ? 'border-l text-left' : 'border-r text-right'}`}>
                                            {parseFloat(po.totalcost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                        </td>
                                        <td className={`p-3 border-x text-center text-gray-500 font-bold ${isRTL ? 'border-l' : 'border-r'}`}>{po.orderddate?.split('T')[0]}</td>
                                        <td className="p-3 text-center text-gray-400 font-black">{po.siteid}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="p-20 text-center font-black text-gray-400 italic uppercase">{t('po.messages.noData')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Footer */}
            <div className={`bg-[#1a3353] text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black uppercase tracking-widest text-[10px] `}>
                <div className={`flex items-center gap-6 `}>
                    <div className={`flex items-center gap-2 `}>
                        <Box size={14} className="text-blue-400"/>
                        <span>{t('po.footer.showing')}: <span className="text-blue-300 mx-1">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r ' : 'border-l'}`}>
                        <Filter size={14} className="text-orange-400"/>
                        <span>{t('common.total')}: <span className="text-orange-300 mx-1">{total}</span></span>
                    </div>
                </div>

                <div className={`flex items-center gap-4 `}>
                    <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 `}>
                        <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <div className={`px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2 `}>
                            <span className="text-orange-400 font-black text-sm">{page}</span>
                            <span className="text-gray-400 text-[9px]">{t('common.of')} {totalPages}</span>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-orange-400 disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Helpers ---
const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => {
    let justifyClass = 'justify-start';
    if (align === 'center') justifyClass = 'justify-center';
    if (align === 'right') justifyClass = isRTL ? 'justify-start' : 'justify-end';
    if (align === 'left' && isRTL) justifyClass = 'justify-end';

    return (
        <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-blue-800 transition-colors ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => onSort(sortKey)}>
            <div className={`flex items-center gap-2 ${justifyClass} `}>
                {label}
                <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-orange-400' : 'text-white/30'} />
            </div>
        </th>
    );
};

const getStatusStyle = (status) => {
    switch (status) {
        case 'COMPLETE': return 'bg-green-100 text-green-800 border-green-200';
        case 'WAPPR': return 'bg-orange-100 text-orange-800 border-orange-200';
        case 'APPR': return 'bg-blue-100 text-blue-800 border-blue-200';
        case 'CLOSE': return 'bg-gray-100 text-gray-800 border-gray-200';
        case 'CAN': return 'bg-red-100 text-red-800 border-red-200';
        default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
};

export default POList;